import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { FOOTER_DISCLOSURE, LEGAL_ENTITY, type LegalDocId } from "../../shared/legal.js";
import { docPath, useStore, type AppStore, type CompanyPageId, type Sheet } from "../state/store.js";
import { todayOpenCount } from "../state/todaycount.js";
import { SCREENS } from "../screens/index.js";
import type { Descriptor } from "./descriptor.js";
import { Avatar } from "./Avatar.js";
import { Background } from "./Background.js";
import { clientTitleFor } from "../../shared/pagemeta.js";
import { lensLine } from "./fragments.js";
import { PopoverHost } from "./Popovers.js";
import { SearchCluster } from "./SearchCluster.js";
import { SheetHost } from "./Sheets.js";

// Today and My Practice are the two screens that are yours. Everything
// below the rule is incoming: things to answer, read, decide, or find.
// Today counts what is still open, the check-list rows left plus the
// undecided suggestion slots, and Feed counts unread in its New lane (both
// 29 Jul 2026). A zero renders blank, so a finished day is quiet.
const NAVDEF: Array<[id: string, icon: string, label: string, count: (s: AppStore) => number | null]> = [
  ["today", "◉", "Today", (s) => (s.appState ? todayOpenCount(s.appState) : null)],
  ["practices", "✓", "My Practice", (s) => s.appState?.counts.practices ?? null],
  ["discover", "✳", "Discover", (s) => s.appState?.counts.discoverNew ?? null],
  // Crossfire ahead of Feed (2 Aug 2026, Rob). Both are reading surfaces, and
  // this is the order of how much of the product they are: Crossfire is a
  // decision the reader records, Feed is what the people they follow put out.
  ["crossfire", "⇄", "Crossfire", (s) => s.appState?.counts.crossfireNew ?? null],
  ["feed", "≋", "Feed", (s) => s.appState?.counts.feedNew ?? null],
  ["follow", "◎", "Follow", () => null],
];

function Lockup() {
  return (
    <div className="lockup">
      <b>Practice</b>
      <small>by Lab 10</small>
    </div>
  );
}

/* Onboarding's chrome (2 Aug 2026, Rob: "take the header out of the top card
   and just put it against background"). The wizard hides the sidebar, so the
   brand and the light/dark switch have to live somewhere, and until today
   that was inside every step's card. Out here it matches the public question
   pages, where the same mark and the same toggle sit on the wallpaper above
   the article: chrome belongs to the page, not to the step, and the card gets
   to open with its own first line instead of a logo.

   The theme control is at the far right and `right` slots in beside it. */
function SoloBar({ right }: { right?: ReactNode }) {
  const flipTheme = useStore((s) => s.flipTheme);
  const nav = useStore((s) => s.nav);
  const user = useStore((s) => s.user);
  return (
    <header className="solobar">
      {/* The logo is a door: Today for the signed in, the front door for
          everyone else. */}
      <button
        className="fbrand brandbtn"
        onClick={() => nav(user ? "today" : "welcome")}
        aria-label="Practice home"
      >
        <span className="mark" aria-hidden="true">
          ✓
        </span>
        <Lockup />
      </button>
      <span className="solobar-r">
        {right}
        <button
          className="circle sm"
          onClick={flipTheme}
          title="Switch between light and dark"
          aria-label="Switch between light and dark"
        >
          ◐
        </button>
      </span>
    </header>
  );
}

function Sidebar({ scr }: { scr: Descriptor }) {
  const state = useStore();
  const { nav, flipTheme } = state;
  const roster = state.experts ?? [];
  const follows = state.appState?.follows ?? [];
  const followList = follows
    .slice(0, 5)
    .map((id) => {
      const e = roster.find((x) => x.id === id);
      return { id, name: e?.name ?? id, sub: e ? lensLine(e) : "", e };
    });
  // People the list does not reach: the ones past the five it holds, plus any
  // row the fit below drops.
  const beyond = follows.length > followList.length;
  const listRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Spec section 9, exception 3: the menu never scrolls because of the
  // follow list; the list is the part that gives way, at every width
  // (desktop included since 28 Jul 2026, when four follows made the
  // sidebar scroll). Whole rows drop from the bottom until the menu fits;
  // measured after every render and on resize, never clipped mid-row.
  //
  // The ellipsis under the list says when that has happened (31 Jul 2026,
  // Rob): a list that quietly stops at whoever fits reads as the whole of who
  // you follow. It is shown before the measuring continues, so its own height
  // is counted in the rows that still fit.
  useEffect(() => {
    const fit = () => {
      const wrap = wrapRef.current;
      const list = listRef.current;
      const head = headRef.current;
      const more = moreRef.current;
      if (!wrap || !list) return;
      const rows = Array.from(list.children) as HTMLElement[];
      rows.forEach((r) => (r.style.display = ""));
      if (head) head.style.display = "";
      if (more) more.style.display = beyond ? "" : "none";
      for (let i = rows.length - 1; i >= 0 && wrap.scrollHeight > wrap.clientHeight; i--) {
        const row = rows[i];
        if (more) more.style.display = "";
        if (row) row.style.display = "none";
      }
      if (head && rows.length && rows.every((r) => r.style.display === "none")) {
        head.style.display = "none";
        if (more) more.style.display = "none";
      }
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  });

  const initials = state.user ? state.user.email.slice(0, 2).toUpperCase() : "··";

  return (
    <aside className="side">
      <div className="sidewrap glass" ref={wrapRef}>
        {/* Theme sits with the brand at the top of the menu, not in the page
            header: it is a preference about the whole app. As a phone
            drawer, the header leads with the hamburger in the same screen
            spot as the bar's, so the button that opened the menu visibly
            closes it (the scrim and Escape still work too). */}
        <div className="brandrow">
          <button
            className="hamb sideclose"
            aria-label="Close menu"
            onClick={() => state.setMenu(false)}
          >
            ☰
          </button>
          <button className="brand" onClick={() => nav("today")}>
            <span className="mark">✓</span>
            <Lockup />
          </button>
          <button
            className="circle sm"
            aria-label="Switch between light and dark"
            title="Switch between light and dark"
            onClick={flipTheme}
          >
            ◐
          </button>
        </div>
        <nav className="main">
          {NAVDEF.map(([id, icon, label, count]) => {
            const n = count(state);
            return (
              <button
                key={id}
                aria-current={scr.nav === id}
                onClick={() => {
                  // The menu entry lands on For You; only the Following
                  // View all button arrives with the Following filter set.
                  if (id === "follow") state.setFollowCat("recommended");
                  nav(id);
                }}
              >
                <span className="ic">{icon}</span>
                {label}
                {n !== null && n !== 0 && <span className="n">{n}</span>}
              </button>
            );
          })}
        </nav>
        {followList.length > 0 && (
          <div className="follow">
            <div className="hd" ref={headRef}>
              <span className="u">Following</span>
              <button
                onClick={() => {
                  state.setFollowCat("following");
                  nav("follow");
                }}
              >
                View all
              </button>
            </div>
            <div className="followlist" ref={listRef}>
              {followList.map((f) => (
                <button key={f.id} className="fperson" onClick={() => nav("expert", f.id)}>
                  {f.e && <Avatar e={f.e} size="m" />}
                  <span>
                    <strong>{f.name}</strong>
                    <small>{f.sub}</small>
                  </span>
                </button>
              ))}
            </div>
            {/* The same door as View all above it: the mark says there are
                more, and pressing it goes to them. */}
            <button
              className="fmore"
              ref={moreRef}
              aria-label="See everyone you follow"
              onClick={() => {
                state.setFollowCat("following");
                nav("follow");
              }}
            >
              •••
            </button>
          </div>
        )}
        <div className="sidefoot">
          <button aria-current={scr.nav === "controls"} onClick={() => nav("controls")}>
            <span style={{ width: 17, textAlign: "center" }}>◳</span>Topic controls
          </button>
          <button aria-current={scr.nav === "review"} onClick={() => nav("review")}>
            <span style={{ width: 17, textAlign: "center" }}>◷</span>Weekly review
          </button>
          {/* A dev convenience only: hidden in production builds. */}
          {import.meta.env.DEV && (
            <button onClick={() => nav("welcome")}>
              <span style={{ width: 17, textAlign: "center" }}>◇</span>Replay onboarding
            </button>
          )}
          {/* The unconfirmed address is carried here, quietly, and nowhere
              else. It gates nothing, so it never appears on Today. */}
          <button className="account" onClick={() => nav("controls")}>
            <span className="av m" style={{ background: "#4A5568" }}>
              {initials}
            </span>
            <span>
              <strong>{state.user ? state.user.email.split("@")[0] : "Guest"}</strong>
              <small>
                {state.user && !state.user.verified ? "Confirm your email" : "Preferences"}
              </small>
            </span>
            <span className="more">•••</span>
          </button>
        </div>
      </div>
    </aside>
  );
}

// One of the five documents, as a link that behaves like a link: middle
// click, cmd-click and "copy link address" all reach the real URL, and a
// plain click opens the modal in place instead of reloading the app.
function DocLink({ doc, page, children }: {
  doc?: LegalDocId;
  page?: CompanyPageId;
  children: ReactNode;
}) {
  const openSheet = useStore((s) => s.openSheet);
  const id = doc ?? page;
  if (!id) return null;
  const sheet: Sheet = doc ? { kind: "legal", doc } : { kind: "company", page: page as CompanyPageId };
  return (
    <a
      href={docPath(id)}
      onClick={(e) => {
        // Let the browser have the ones the reader meant for the browser.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        openSheet(sheet);
      }}
    >
      {children}
    </a>
  );
}

// Standard company footer (28 Jul 2026): the dashboard has its own nav, so
// the footer carries the company pages. Photo credits live on About now.
function Footer() {
  const nav = useStore((s) => s.nav);
  const user = useStore((s) => s.user);
  return (
    <footer className="foot">
      {/* No .glass (2 Aug 2026, Rob): the footer is not a panel. See
          .foot .inner in app.css. */}
      <div className="inner">
        <div className="ftop">
          {/* The lockup and the disclosure share this block so they sit side
              by side, the sentence keeping a straight left edge of its own
              (2 Aug 2026, Rob). Only the copyright goes under the mark, on the
              row below. */}
          <div className="fmain">
            <button
              className="fbrand brandbtn"
              aria-label="Practice home"
              onClick={() => nav(user ? "today" : "welcome")}
            >
              <span className="mark" aria-hidden="true">
                ✓
              </span>
              <Lockup />
            </button>
            {/* Counsel's standing disclosure (1 Aug 2026). It replaced
                "Practice organizes what the experts you follow have said",
                which named neither the automated processing nor the absence
                of any endorsement, the two things this line exists to say. */}
            <p className="fnote">{FOOTER_DISCLOSURE}</p>
          </div>
          {/* Documents and copyright as one right-hand cluster (2 Aug 2026,
              Rob), the copyright last. It is the same weight of small print
              as the links beside it, so the three tries at giving it a row of
              its own (right of the disclosure, centred, under the lockup) all
              came to the same thing: a single quiet line looking for company.

              It names the entity, not the brand: a brand cannot own a
              copyright, and this is the one line in the UI where the legal
              person belongs rather than the trade name. "Lab 10" alone would
              have been enough (17 U.S.C. 401(b)(3) accepts an abbreviation the
              owner is recognized by), but it also makes who operates this
              identifiable without opening a policy. */}
          <div className="fright">
            <nav className="flinks">
              {/* Modals, not destinations (1 Aug 2026, Rob): the footer is on
                  every screen, so reading any of these should never cost you
                  the screen you were on. Real anchors all the same (2 Aug
                  2026), because every one is a real address: /terms opens the
                  terms over whatever you are on, and the same URL in a mail or
                  a store listing does the same thing from cold. A modal that
                  cannot be linked to is a page nobody can send anyone. */}
              <DocLink page="about">About</DocLink>
              <DocLink page="contact">Contact</DocLink>
              <DocLink doc="privacy">Privacy policy</DocLink>
              {/* The consumer health data policy carries its own prominent
                  link on every screen, which is what Washington's MHMDA asks
                  for: it is a distinct document, not a section of the privacy
                  policy, and burying it would defeat the point. */}
              <DocLink doc="health">Health data</DocLink>
              <DocLink doc="terms">Terms of use</DocLink>
              {!user && <button onClick={() => nav("signin")}>Sign in</button>}
            </nav>
            <a className="fmeta" href="https://lab10.com" target="_blank" rel="noopener noreferrer">
              © 2026 {LEGAL_ENTITY}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// The filter bar scrolls sideways when the tabs outgrow the screen, with the
// scrollbar hidden; without help the last tab just stops mid-word (reported
// 29 Jul 2026). Compression escalates: first the counts drop (only on narrow
// screens, and only once the bar with counts would not fit, never sooner),
// and whatever still hides dissolves at the edge, with an ellipsis on the
// right saying so. On desktop the numbers always show (Rob, 29 Jul 2026):
// an overflowing bar scrolls instead.
function SegBar({ tabs }: { tabs: NonNullable<Descriptor["tabs"]> }) {
  const ref = useRef<HTMLDivElement>(null);
  // The bar's content width with counts showing: only measurable while they
  // show, so it is remembered for deciding the way back. Layout effect, so
  // the first frame never paints counts it is about to take away.
  const natural = useRef(0);
  const [tight, setTight] = useState(false);
  const [clip, setClip] = useState({ left: false, right: false });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      if (!tight) natural.current = el.scrollWidth;
      const mobile = window.matchMedia("(max-width: 920px)").matches;
      setTight(mobile && natural.current > el.clientWidth + 1);
      const left = el.scrollLeft > 1;
      const right = el.scrollWidth - el.clientWidth - el.scrollLeft > 1;
      setClip((c) => (c.left === left && c.right === right ? c : { left, right }));
    };
    check();
    el.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      el.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  });
  return (
    <div className={`segwrap${clip.left ? " clipl" : ""}${clip.right ? " clipr" : ""}`}>
      <div className={`seg${tight ? " tight" : ""}${tabs.equal ? " equal" : ""}`} ref={ref}>
        {tabs.items.map(([id, label]) => (
          <button key={id} aria-selected={tabs.active === id} onClick={() => tabs.onPick(id)}>
            {label}
            {tabs.counts[id] !== undefined && <span className="n">{tabs.counts[id]}</span>}
          </button>
        ))}
        {tabs.extra}
      </div>
    </div>
  );
}

export function Shell() {
  const state = useStore();
  const { screen, arg, menuOpen, setMenu, goBack } = state;

  const screenFn = SCREENS[screen] ?? SCREENS.today;
  if (!screenFn) throw new Error("no screens registered");
  const scr = screenFn(state, arg);

  /* The tab says where you are (2 Aug 2026). The server puts a title in the
     document for the URL that was requested; from here on the navigation is
     client-side and nothing else would ever change it, so six tabs of the app
     were six tabs called "Practice" and the back-button history was a column
     of identical rows. Same table the server reads, so the two agree.

     "/" is the one URL that means two things: the front door to a visitor,
     Today to an account, and only this side knows which. */
  const signedIn = Boolean(state.user);
  useEffect(() => {
    document.title = clientTitleFor(screen, signedIn);
  }, [screen, signedIn]);

  // Spec section 9, exception 1: the search cluster is one live element
  // re-parented between the page header and the mobile bar, so rebuilding
  // the header can never destroy it. The portal container node moves; the
  // React subtree inside it stays mounted. A ref, not useMemo: Fast
  // Refresh drops memo caches but keeps refs, and a dev hot update that
  // reminted this node left the stale one glowing in the header.
  const actsRef = useRef<HTMLDivElement | null>(null);
  if (!actsRef.current) {
    actsRef.current = document.createElement("div");
    actsRef.current.className = "acts";
  }
  const actsEl = actsRef.current;
  const mobileActsRef = useRef<HTMLDivElement>(null);
  const cheadActsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const place = () => {
      const narrow = window.matchMedia("(max-width:920px)").matches;
      const target = (!narrow && cheadActsRef.current) || mobileActsRef.current;
      if (target && actsEl.parentElement !== target) target.appendChild(actsEl);
    };
    place();
    const mq = window.matchMedia("(max-width:920px)");
    mq.addEventListener("change", place);
    return () => mq.removeEventListener("change", place);
  });

  const themeApplied = useRef(false);
  useEffect(() => {
    const root = document.documentElement;
    // On an actual flip (not first paint), a temporary .theming class puts
    // a transition on every colour-bearing property so the switch fades
    // instead of snapping. Removed after the fade so hover and press
    // timings elsewhere stay their own.
    if (themeApplied.current) {
      root.classList.add("theming");
      const t = window.setTimeout(() => root.classList.remove("theming"), 2100);
      root.setAttribute("data-theme", state.theme);
      return () => {
        window.clearTimeout(t);
        root.classList.remove("theming");
      };
    }
    themeApplied.current = true;
    root.setAttribute("data-theme", state.theme);
  }, [state.theme]);

  // The drawer state lives on the root element: the scrim and the sliding
  // .side panel are both styled off .menuopen, same as the reference.
  useEffect(() => {
    document.documentElement.classList.toggle("menuopen", menuOpen);
  }, [menuOpen]);

  // A modal is a decision, so the page behind it holds still: a wheel or a
  // swipe over the scrim used to scroll the surface underneath and lose the
  // row the sheet came from. The body is pinned rather than merely
  // overflow-hidden, which is the only version iOS honours, and the
  // scroll position is put back on close so nothing moves. The width the
  // scrollbar gives up is paid back as padding so the page cannot jump
  // sideways as the sheet opens.
  const sheetOpen = state.sheet !== null;
  useEffect(() => {
    if (!sheetOpen) return;
    const root = document.documentElement;
    const y = window.scrollY;
    const where = () => {
      const s = useStore.getState();
      return `${s.screen}:${s.arg ?? ""}`;
    };
    const from = where();
    const bar = window.innerWidth - root.clientWidth;
    root.style.setProperty("--lockpad", `${bar}px`);
    root.style.setProperty("--locktop", `-${y}px`);
    root.classList.add("sheetopen");
    return () => {
      root.classList.remove("sheetopen");
      root.style.removeProperty("--lockpad");
      root.style.removeProperty("--locktop");
      // The few sheets that hand off to a screen (an expert, a crossfire
      // question) close first, so nav's own reset lands on a pinned body
      // and does nothing. A new screen starts at the top; the surface you
      // came from gets its place back.
      window.scrollTo(0, where() === from ? y : 0);
    };
  }, [sheetOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenu(false);
        state.setSearchOpen(false);
        state.closeSheet();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const isSub = Boolean(scr.back) && !scr.solo;
  const backCall = () => goBack(scr.back ?? "today", scr.backArg ?? null);

  let head = null;
  if (!scr.solo) {
    head = !scr.hideHead ? (
      <div className="chead">
        <div>
          {scr.back && (
            <button className="backlink" onClick={backCall}>
              ‹ Back
            </button>
          )}
          <h1>{scr.title}</h1>
        </div>
        <div ref={cheadActsRef} style={{ display: "contents" }} />
      </div>
    ) : (
      <div className="chead">
        <div>
          <button className="backlink" onClick={backCall}>
            ‹ Back
          </button>
        </div>
        <div ref={cheadActsRef} style={{ display: "contents" }} />
      </div>
    );
  }

  // Keyed by screen: the remembered natural width belongs to one screen's
  // tabs, and must not decide another's.
  const seg = scr.tabs ? <SegBar key={scr.nav ?? scr.title} tabs={scr.tabs} /> : null;
  // Actions share the filter bar's row when the screen offers them: to its
  // right while there is room, then a right-justified row of their own.
  const tabs = scr.tabs?.aside ? (
    <div className="segrow">
      {seg}
      <span className="segside">{scr.tabs.aside}</span>
    </div>
  ) : (
    seg
  );

  return (
    <>
      <Background />
      {/* Solo screens (onboarding, sign-in) take the brand bar instead of the
          app's mobile bar: no menu to open, no screen title to carry, and the
          lockup is the only piece of chrome they need. */}
      {scr.solo && <SoloBar right={scr.soloRight} />}
      {!scr.solo && (
        <div className="mobilebar">
          <div className="mleft">
            <button
              className="hamb"
              aria-label={isSub ? "Back" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={isSub ? backCall : () => setMenu(!menuOpen)}
            >
              {isSub ? "‹" : "☰"}
            </button>
          </div>
          <div>
            <div className="mtitle">{scr.title ?? ""}</div>
          </div>
          <div ref={mobileActsRef} style={{ display: "contents" }} />
        </div>
      )}
      <div className="scrim" aria-hidden="true" onClick={() => setMenu(false)} />
      <div className={`stage${scr.solo ? " solo" : ""}${!scr.solo && !scr.rail ? " wide" : ""}`}>
        <Sidebar scr={scr} />
        <main className="content">
          {head}
          {scr.hero}
          {scr.bar}
          {tabs}
          {scr.chips && <div className="chiprow">{scr.chips}</div>}
          {scr.main}
        </main>
        <aside className="rail">{scr.rail && <div className="railwrap">{scr.rail}</div>}</aside>
      </div>
      <Footer />
      <SheetHost />
      <PopoverHost />
      {/* Search belongs to the signed-in app: on solo screens (onboarding,
          company pages) the cluster would portal into a bar with no header
          anchor and strand the icon somewhere arbitrary. */}
      {!scr.solo && createPortal(<SearchCluster />, actsEl)}
    </>
  );
}


